import { createContext, useContext } from 'react';
import type { FallenChoice } from '@sorrem/shared';

import type { UIItemSlotRef } from '@sorrem-client/types';
import type { GameStateBridge } from '@sorrem-client/ui/shared/game-state-bridge';
import type { StartCraftResult } from '@sorrem-client/game/protocol';

export interface DebugOverlayCharacterOption {
    readonly id: string;
    readonly name: string;
}

export interface DebugOverlayPartyMemberOption {
    readonly id: string;
    readonly name: string;
}

export interface DebugOverlayControls {
    readonly characters: readonly DebugOverlayCharacterOption[];
    readonly selectedCharacterId: string | null;
    readonly onSelectCharacter: (characterId: string) => void;
    readonly partyMembers: readonly DebugOverlayPartyMemberOption[];
    readonly onAddPartyMember: (characterId: string) => void;
    readonly onRemovePartyMember: (characterId: string) => void;
}

export interface GameplayContextValue {
    bridge: GameStateBridge;
    /** The crucible whose run this is (the session's own); null in a debug preview, which has none. */
    crucibleId: string | null;
    requestInventoryMove: (requestId: string, itemId: string, target: UIItemSlotRef) => Promise<boolean>;
    /**
     * Starts using one crystal from a carried crystal stack (e.g. an action-bar
     * hotkey linked to its slot); it takes effect once the use channel completes.
     */
    requestConsumeCrystal: (itemId: string) => void;
    /** Drops a whole carried item at the player's feet, where the party can pick it up. */
    requestDropItem: (itemId: string) => void;
    /** Cancels the crystal use in progress (its hotkey was let go); the crystal is kept. */
    requestCancelCrystalUse: () => void;
    /** Casts the Essence ability of skill `skillId` (an action-bar hotkey linked to the skill). */
    requestCastAbility: (skillId: string) => void;
    /** Lets go of an ability cast by holding (its hotkey was let go before it went off); nothing is spent. */
    requestCancelCastAbility: () => void;
    /** Tells the Crucible the player is quitting, so it doesn't hold their place for a rejoin. */
    requestLeaveRun: () => void;
    /** A fallen player's choice: re-manifest where they fell, or leave the run. */
    requestFallenChoice: (choice: FallenChoice) => void;
    /** The intro's HUD tour came on screen or left it (#234): the Crucible holds its enemies meanwhile. */
    requestTourHold: (active: boolean) => void;
    /** Asks the Crucible to coalesce a crystal from a recipe into a carried slot; resolves when it answers. */
    requestStartCraft: (slotIndex: number, recipeId: string) => Promise<StartCraftResult>;
    /** Cancels a crystal still coalescing and refunds its essence. */
    requestCancelCraft: (coalescenceId: number) => void;
    debugControls?: DebugOverlayControls;
}

export const GameplayContext = createContext<GameplayContextValue | null>(null);

export function useGameplay(): GameplayContextValue {
    const ctx = useContext(GameplayContext);
    if (ctx === null) {
        throw new Error('useGameplay must be used within a gameplay surface');
    }
    return ctx;
}
